import { useCallback, useEffect, useId, useMemo } from 'react';

import { ExternalLinkIcon, PhoneOffIcon } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from '@/components/atoms/Accordion/Accordion';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import usePrompt from '@/hooks/usePrompt';
import { setDirty, setExpanded } from '@/redux/integrations/actions';
import { selectAccordion } from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import {
  AccordionFieldsetProps,
  Integration,
  IntegrationType,
  WebWidgetIntegration,
} from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

type Form = { voice_integration_id: string | null };

const Voice = ({
  type,
  integration,
  toggleAccordion,
  registerAccordion,
}: AccordionFieldsetProps<Form, WebWidgetIntegration>) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const deskId = useDeskId();
  const { expanded } = useSelector(selectAccordion);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const dispatch = useDispatch();
  const enableLabelId = useId();
  const { updateIntegration, updateStatus, integrations } = useIntegrations<WebWidgetIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );

  // web-voice integrations on this desk are the candidates the widget can pair
  // with. With none, we prompt the user to create one instead.
  const webVoiceIntegrations = useMemo(
    () =>
      ((integrations ?? []) as Integration[]).filter(
        (item) => item.type === IntegrationType.WEB_VOICE
      ),
    [integrations]
  );
  const hasVoiceIntegrations = webVoiceIntegrations.length > 0;

  const formMethods = useForm<Form>({ mode: 'onSubmit' });

  const {
    reset,
    formState: { isDirty },
    control,
    handleSubmit,
    setValue,
    trigger,
  } = formMethods;

  const voiceIntegrationId = useWatch({ control, name: 'voice_integration_id' });

  const selectedVoiceIntegration = webVoiceIntegrations.find(
    (item) => item.integration_id === voiceIntegrationId
  );
  // The saved pairing points at an integration that no longer exists on the desk.
  const voiceIntegrationMissing = Boolean(voiceIntegrationId) && !selectedVoiceIntegration;
  // The paired integration exists but is turned off — the voice button stays hidden.
  const voiceIntegrationInactive = Boolean(
    selectedVoiceIntegration && !selectedVoiceIntegration.active
  );
  // A desk normally has a single web-voice connection, so the pairing is
  // unambiguous. Show the picker only when there is a real choice — more than
  // one connection, or a saved pairing that no longer exists and needs fixing.
  const showIntegrationPicker =
    Boolean(voiceIntegrationId) && (webVoiceIntegrations.length > 1 || voiceIntegrationMissing);

  useEffect(() => {
    reset({ voice_integration_id: integration?.config?.voice_integration_id ?? null });
  }, [integration?.config?.voice_integration_id, reset]);

  const handleToggleVoice = useCallback(
    (checked: boolean) => {
      if (!canWrite) {
        return;
      }
      if (!checked) {
        setValue('voice_integration_id', null, { shouldDirty: true });
        return;
      }
      // Default to the first active integration so the snippet is usable right away.
      const preferred = webVoiceIntegrations.find((item) => item.active) ?? webVoiceIntegrations[0];
      setValue('voice_integration_id', preferred?.integration_id ?? null, { shouldDirty: true });
    },
    [canWrite, setValue, webVoiceIntegrations]
  );

  const handleSelectVoice = useCallback(
    (value: string) => {
      setValue('voice_integration_id', value, { shouldDirty: true });
    },
    [setValue]
  );

  // Lets the user drop a pairing that points at a now-deleted integration from
  // the empty state, where there is no toggle or picker left to clear it with.
  const handleClearVoicePairing = useCallback(() => {
    setValue('voice_integration_id', null, { shouldDirty: true });
  }, [setValue]);

  const handleUpdateIntegration = useCallback(() => {
    const formValues = formMethods.getValues();
    updateIntegration(
      {
        ...integration,
        config: {
          ...integration?.config,
          // null clears the pairing; the backend persists it on the web config.
          voice_integration_id: formValues.voice_integration_id || null,
        },
      },
      {
        onSuccess: () => {
          if (expanded === type) {
            dispatch(setExpanded(false));
          }
        },
      }
    );
  }, [dispatch, expanded, formMethods, integration, type, updateIntegration]);

  usePrompt(isDirty && canWrite, undefined, undefined, handleSubmit(handleUpdateIntegration));

  useEffect(() => {
    dispatch(setDirty(isDirty));
  }, [dispatch, isDirty]);

  useEffect(() => {
    if (expanded !== type && isDirty) {
      reset();
    }
  }, [expanded, isDirty, reset, type]);

  return (
    <Accordion
      title={t('integrations.web_voice.title')}
      subtitle={t('integrations.web_voice.subtitle')}
      footerText={t('integrations.web_voice.footer')}
      onSubmit={handleSubmit(handleUpdateIntegration)}
      disabled={!isDirty}
      disableFooter={!hasVoiceIntegrations && !isDirty && !voiceIntegrationMissing}
      isLoading={updateStatus === 'pending'}
      expanded={expanded === type}
      handleChange={toggleAccordion(type)}
      readOnly={!canWrite}
      ref={registerAccordion(type, handleSubmit(handleUpdateIntegration), trigger)}
    >
      {!hasVoiceIntegrations && (
        <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
          <div className="mb-2 flex size-8 items-center justify-center rounded-lg bg-bg-muted">
            <PhoneOffIcon size={16} className="text-foreground-muted" />
          </div>
          <h3 className="text-base leading-6 font-semibold text-foreground">
            {t('integrations.web_voice.empty_title')}
          </h3>
          <p className="max-w-sm text-sm leading-5 text-foreground-muted">
            {t('integrations.web_voice.empty_subtitle')}
          </p>
          <Button
            variant="link"
            asChild
            className="mt-2 h-auto gap-1 p-0 text-accent [&_svg]:text-accent"
          >
            <a
              href={`/${slug}/environments/${deskId}/integrations`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('integrations.web_voice.empty_cta')}
              <ExternalLinkIcon />
            </a>
          </Button>
          {voiceIntegrationMissing && (
            <div className="mt-2 flex flex-col items-center gap-2">
              <p className="max-w-sm text-sm leading-5 text-destructive">
                {t('integrations.web_voice.empty_stale_warning')}
              </p>
              <Button
                variant="outline-neutral"
                onClick={handleClearVoicePairing}
                disabled={!canWrite}
              >
                {t('integrations.web_voice.clear_pairing')}
              </Button>
            </div>
          )}
        </div>
      )}

      {hasVoiceIntegrations && (
        <>
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col items-start gap-1">
              <span id={enableLabelId} className="text-sm leading-5 font-medium text-foreground">
                {t('integrations.web_voice.enable')}
              </span>
              {selectedVoiceIntegration && (
                <Button
                  variant="link"
                  asChild
                  className="h-auto gap-1 p-0 text-accent [&_svg]:text-accent"
                >
                  <a
                    href={`/${slug}/environments/${deskId}/integrations/${selectedVoiceIntegration.integration_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('integrations.web_voice.open_integration')}
                    <ExternalLinkIcon />
                  </a>
                </Button>
              )}
            </div>
            <Switch
              checked={Boolean(voiceIntegrationId)}
              onCheckedChange={handleToggleVoice}
              disabled={!canWrite}
              aria-labelledby={enableLabelId}
            />
          </div>

          {(showIntegrationPicker || voiceIntegrationInactive) && (
            <div className="mt-2 flex flex-col gap-1">
              {showIntegrationPicker && (
                <div className="w-96">
                  <Select
                    value={selectedVoiceIntegration?.integration_id ?? ''}
                    onValueChange={handleSelectVoice}
                    disabled={!canWrite}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('integrations.web_voice.select_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {webVoiceIntegrations.map((item) => (
                        <SelectItem key={item.integration_id} value={item.integration_id}>
                          {item.active
                            ? item.name
                            : t('integrations.web_voice.option_inactive', { 0: item.name })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {voiceIntegrationMissing && (
                <p className="mt-1 text-sm text-destructive">
                  {t('integrations.web_voice.missing_warning')}
                </p>
              )}
              {voiceIntegrationInactive && (
                <p className="mt-1 text-sm text-warning">
                  {t('integrations.web_voice.inactive_warning')}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </Accordion>
  );
};

export default Voice;
